import React, { useRef, useState, useCallback } from "react";
import { InsertDriveFile } from "@mui/icons-material";

interface FileUploadSectionProps {
  onFileSelect: (file: File) => void;
  isProcessing: boolean;
  selectedFile: File | null;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  onFileSelect,
  isProcessing,
  selectedFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
    },
    [onFileSelect]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];

        // Validate file type
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        if (!fileExtension || !["xlsx", "xls"].includes(fileExtension)) {
          alert("Please upload an Excel file (.xlsx or .xls)");
          return;
        }

        onFileSelect(file);
      }
    },
    [onFileSelect]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div>
      {selectedFile ? (
        <div className="file-info">
          <InsertDriveFile className="file-icon" />
          <div className="file-details">
            <h4>{selectedFile.name}</h4>
            <p>
              Size: {formatFileSize(selectedFile.size)} • Type: {selectedFile.type || "Excel File"}
            </p>
          </div>
        </div>
      ) : (
        <div
          className={`upload-zone ${isDragOver ? "drag-over" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          <div className="upload-text">
            {isDragOver ? "Drop your Excel file here" : "Click to upload or drag and drop"}
          </div>
          <div className="upload-subtext">Supports .xlsx and .xls files up to 5MB</div>
          <button className="upload-button" disabled={isProcessing} type="button">
            {isProcessing ? "Processing..." : "Select File"}
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        className="hidden-input"
        disabled={isProcessing}
      />

      {selectedFile && !isProcessing && (
        <div style={{ textAlign: "center", marginTop: "15px" }}>
          <button className="upload-button" onClick={handleUploadClick} type="button">
            Choose Different File
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;
